import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Users,
  Package,
  UsersRound,
  Settings,
  Search,
  Building2,
  Boxes,
} from "lucide-react";
import { trpc } from "../../lib/trpc";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

const NAV = [
  { label: "Dashboard", to: "/provider/dashboard", icon: LayoutDashboard },
  { label: "Clients", to: "/provider/clients", icon: Users },
  { label: "Catalogue", to: "/provider/catalogue", icon: Package },
  { label: "Team", to: "/provider/team", icon: UsersRound },
  { label: "Settings", to: "/provider/settings", icon: Settings },
] as const;

/** ⌘K command palette + its trigger button. Searches navigation, clients & services. */
export function CommandMenu(): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Loaded once and cached; cmdk filters client-side as you type.
  const clientsQ = trpc.tenant.provider.listClients.useQuery(undefined, {
    enabled: open,
  });
  const servicesQ = trpc.catalogue.listServices.useQuery({}, { enabled: open });
  const clients =
    (clientsQ.data as { id: string; name: string }[] | undefined) ?? [];
  const services =
    (servicesQ.data as { id: string; name: string }[] | undefined) ?? [];

  useEffect(() => {
    const down = (e: KeyboardEvent): void => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const go = (fn: () => void): void => {
    setOpen(false);
    fn();
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-muted-foreground gap-2"
      >
        <Search className="size-4" />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="pointer-events-none hidden h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:inline-flex">
          ⌘K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search clients, services, pages…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          <CommandGroup heading="Navigation">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.to}
                  value={`nav ${item.label}`}
                  onSelect={() => go(() => void navigate({ to: item.to }))}
                >
                  <Icon />
                  <span>{item.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>

          {clients.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Clients">
                {clients.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`client ${c.name}`}
                    onSelect={() =>
                      go(
                        () =>
                          void navigate({
                            to: "/provider/clients/$id",
                            params: { id: c.id },
                          }),
                      )
                    }
                  >
                    <Building2 />
                    <span>{c.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {services.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Services">
                {services.map((s) => (
                  <CommandItem
                    key={s.id}
                    value={`service ${s.name}`}
                    onSelect={() =>
                      go(
                        () =>
                          void navigate({
                            to: "/provider/catalogue/services/$id",
                            params: { id: s.id },
                          }),
                      )
                    }
                  >
                    <Boxes />
                    <span>{s.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
